import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import Breadcrumbs from "../../../components/ui/Breadcrumbs.jsx";
import BackDrop from "../../../components/ui/BackDrop";
import MirageLoader from "../../../components/ui/pageLoader/MirageLoader";
import Table from "../../../components/ui/table/Table";
import CustomDateInput from "../../../components/ui/custom-date-input/CustomDateInput";
import SearchableDropdown from "../../../components/ui/tableDataDropdowns/SearchableDropdown";
import { FaSync } from "react-icons/fa";
import { errorNotify, successNotify } from "../../../Services/Notification.js";
import {
  closeIcon,
  contractAddIcon,
  contractImportIcon,
  editPencilIcon,
  fileUploadIcon,
  visibilityIcon,
} from "../../../assetData/icons";
import { cn } from "../../../utils/twMerge";

const pmsApiBaseUrl = `${process.env.REACT_APP_API_URL}api/pms`;

const initialPmsForm = {
  name: "",
  license_number: "",
  license_from_date: "",
  license_to_date: "",
  short_name: "",
  address: "",
  email: "",
  mobile: "",
  status: true,
};

const initialSchemeForm = {
  pms: "",
  name: "",
  code: "",
};

const getHeaders = (contentType = "application/json") => {
  const token = sessionStorage.getItem("subscriberToken")
    ? sessionStorage.getItem("subscriberToken")
    : sessionStorage.getItem("token");

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (contentType) headers["Content-Type"] = contentType;

  return headers;
};

const getPmsSchemes = (pms) =>
  pms?.schemes || pms?.scheme || pms?.scheme_list || pms?.children || [];

const getPmsName = (pms) => pms?.name || pms?.pms || pms?.pms_name || "";
const getSchemeName = (scheme) => scheme?.name || scheme?.scheme_name || "";
const getSchemeCode = (scheme) => scheme?.code || scheme?.scheme_code || "";
const normalizeDate = (date) => (date ? String(date).slice(0, 10) : "");
const getStatusLabel = (status) =>
  status === false || status === "false" || status === "Inactive"
    ? "Inactive"
    : "Active";

const buildPmsPayload = (data) => {
  const payload = {
    name: data?.name || "",
    license_number: data?.license_number || "",
    license_from_date: data?.license_from_date || "",
    license_to_date: data?.license_to_date || "",
    short_name: data?.short_name || "",
    address: data?.address || "",
    email: data?.email || "",
    mobile: data?.mobile || "",
  };

  if (data?.id) {
    payload.id = data.id;
    payload.status = data?.status === true || data?.status === "true";
  }

  return payload;
};

const buildSchemePayload = (data) => {
  const payload = {
    pms: Number(data?.pms),
    name: data?.name || data?.scheme_name || "",
    code: data?.code || data?.scheme_code || "",
  };

  if (data?.id) {
    payload.id = data.id;
    payload.status = data?.status === true || data?.status === "true";
  }

  return payload;
};

const DateField = ({
  label,
  value,
  onChange,
  required,
  readOnly,
  min,
  max,
}) => (
  <div className="flex w-full flex-col gap-2">
    <label className="text-base text-[#636363]">
      {label} {required && "*"}
    </label>
    <CustomDateInput
      initialValue={value}
      min={min}
      max={max}
      disabled={readOnly}
      handleSetValue={onChange}
      className={cn(
        "h-full w-full rounded bg-neutral-100 p-2 px-2 text-[#636363] focus:outline-none",
        readOnly && "cursor-not-allowed opacity-80",
      )}
    />
  </div>
);

const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required,
  readOnly,
}) => (
  <div className="flex w-full flex-col gap-2">
    <label className="text-base text-[#636363]">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      readOnly={readOnly}
      className={cn(
        "h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] placeholder:italic focus:outline-none",
        readOnly && "cursor-not-allowed opacity-80",
      )}
    />
  </div>
);

const PmsFormPopup = ({ mode, data, onClose, onSubmit, isButtonClicked }) => {
  const [formData, setFormData] = useState({
    ...initialPmsForm,
    ...data,
    license_from_date: normalizeDate(data?.license_from_date),
    license_to_date: normalizeDate(data?.license_to_date),
  });
  const isView = mode === "view";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (isView) return;

    const res = await onSubmit(buildPmsPayload(formData));
    if (res) onClose();
  };

  return (
    <>
      <div className="fixed left-1/2 top-1/2 z-50 mx-auto min-h-[300px] w-[98%] max-w-[1000px] -translate-x-1/2 rounded bg-white p-8 max-lg:top-[10%] lg:-translate-y-1/2">
        <h2 className="pb-4 text-2xl font-bold text-neutral-800">
          {isView ? "View PMS" : mode === "edit" ? "Edit PMS" : "Add PMS"}
        </h2>
        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} alt="close" />
        </button>

        <form onSubmit={submitHandler}>
          <div className="scrollbar-custom max-lg:max-h-[500px] max-lg:overflow-y-scroll">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <InputField
                label="PMS Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                readOnly={isView}
              />
              <InputField
                label="Short Name"
                name="short_name"
                value={formData.short_name}
                onChange={handleChange}
                readOnly={isView}
              />
              <InputField
                label="License Number"
                name="license_number"
                value={formData.license_number}
                onChange={handleChange}
                required
                readOnly={isView}
              />
              <DateField
                label="License From Date"
                value={formData.license_from_date}
                onChange={(value) =>
                  handleDateChange("license_from_date", value)
                }
                max={formData.license_to_date}
                required
                readOnly={isView}
              />
              <DateField
                label="License To Date"
                value={formData.license_to_date}
                onChange={(value) => handleDateChange("license_to_date", value)}
                min={formData.license_from_date}
                required
                readOnly={isView}
              />
              <InputField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                readOnly={isView}
              />
              <InputField
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                readOnly={isView}
              />
              <div className="flex w-full flex-col gap-2 md:col-span-2">
                <label className="text-base text-[#636363]">Address</label>
                <textarea
                  name="address"
                  value={formData.address ?? ""}
                  onChange={handleChange}
                  readOnly={isView}
                  className="min-h-[42px] rounded bg-neutral-100 p-2 px-2 text-[#636363] focus:outline-none"
                />
              </div>
              {mode !== "add" && (
                <div className="flex w-full flex-col gap-2">
                  <label className="text-[#636363]">Status</label>
                  <select
                    name="status"
                    value={String(formData.status ?? true)}
                    onChange={handleChange}
                    disabled={isView}
                    className="h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] focus:outline-none disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          {!isView && (
            <button
              type="submit"
              disabled={isButtonClicked}
              className={`${
                isButtonClicked ? "opacity-[70%]" : ""
              } mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-16 py-2 font-semibold text-white md:w-1/3`}
            >
              {isButtonClicked ? (
                <MirageLoader />
              ) : mode === "edit" ? (
                "UPDATE"
              ) : (
                "ADD"
              )}
            </button>
          )}
        </form>
      </div>
      <BackDrop />
    </>
  );
};

const SchemeFormPopup = ({
  mode,
  data,
  pmsOptions,
  onClose,
  onSubmit,
  isButtonClicked,
}) => {
  const [formData, setFormData] = useState({
    ...initialSchemeForm,
    ...data,
    status: data?.status ?? true,
  });
  const isView = mode === "view";
  const pmsDropdownOptions = pmsOptions?.map((pms) => ({
    ...pms,
    name: getPmsName(pms),
  }));
  const selectedPms = pmsOptions?.find(
    (pms) => String(pms.id) === String(formData.pms),
  );
  const selectedPmsName = getPmsName(selectedPms);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (isView) return;
    if (!formData.pms) {
      errorNotify("Please select PMS.");
      return;
    }
    const res = await onSubmit(buildSchemePayload(formData));
    if (res) onClose();
  };

  return (
    <>
      <div className="fixed left-1/2 top-1/2 z-50 mx-auto min-h-[300px] w-[98%] max-w-[900px] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-8">
        <h2 className="pb-4 text-2xl font-bold text-neutral-800">
          {isView
            ? "View Scheme"
            : mode === "edit"
            ? "Edit Scheme"
            : "Add Scheme"}
        </h2>
        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} alt="close" />
        </button>

        <form onSubmit={submitHandler}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex w-full flex-col gap-2">
              <label className="text-[#636363]">PMS *</label>
              {mode === "add" ? (
                <SearchableDropdown
                  data={pmsDropdownOptions}
                  valueKey="id"
                  displayKey="name"
                  filterKey="pms"
                  setFilter={(val) =>
                    setFormData((prev) => ({ ...prev, pms: val || "" }))
                  }
                  label="PMS"
                  type="searchable"
                />
              ) : (
                <input
                  value={selectedPmsName}
                  readOnly
                  className="h-full cursor-not-allowed rounded bg-neutral-100 p-2 px-2 text-[#636363] opacity-80 focus:outline-none"
                />
              )}
            </div>
            <InputField
              label="License Number"
              name="license_number"
              value={selectedPms?.license_number || ""}
              onChange={() => {}}
              readOnly
            />
            <InputField
              label="Scheme Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              readOnly={isView}
            />
            <InputField
              label="Code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              readOnly={isView}
            />
            {mode !== "add" && (
              <div className="flex w-full flex-col gap-2">
                <label className="text-[#636363]">Status</label>
                <select
                  name="status"
                  value={String(formData.status ?? true)}
                  onChange={handleChange}
                  disabled={isView}
                  className="h-full rounded bg-neutral-100 p-2 px-2 text-[#636363] focus:outline-none disabled:cursor-not-allowed disabled:opacity-80"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            )}
          </div>

          {!isView && (
            <button
              type="submit"
              disabled={isButtonClicked}
              className={`${
                isButtonClicked ? "opacity-[70%]" : ""
              } mt-8 w-full rounded border-2 border-pri-clr-orange bg-pri-clr-orange px-16 py-2 font-semibold text-white md:w-1/3`}
            >
              {isButtonClicked ? (
                <MirageLoader />
              ) : mode === "edit" ? (
                "UPDATE"
              ) : (
                "ADD"
              )}
            </button>
          )}
        </form>
      </div>
      <BackDrop />
    </>
  );
};

const ImportPmsPopup = ({ onClose, onSubmit }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async () => {
    if (!selectedFile) {
      errorNotify("Please select excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    setIsLoading(true);
    const res = await onSubmit(formData);
    setIsLoading(false);
    if (res) onClose();
  };

  return (
    <>
      <div className="fixed left-1/2 top-1/2 z-50 mx-auto min-h-[300px] w-[500px] max-w-[95%] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-8">
        <h2 className="pb-4 text-center text-2xl font-bold text-neutral-800">
          Import PMS
        </h2>
        <button
          type="button"
          className="absolute right-8 top-8"
          onClick={onClose}
        >
          <img src={closeIcon} alt="close" />
        </button>
        <div className="mt-8 space-y-4">
          <div className="relative overflow-hidden rounded-lg">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="custom-doc-uploader text-neutral-600"
              onChange={(e) => setSelectedFile(e.target.files?.[0])}
            />
            <img
              src={fileUploadIcon}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2"
              alt="upload"
            />
          </div>
          <button
            className="w-full rounded-lg border-[1px] border-[#25A86A] py-2 font-bold text-[#25A86A]"
            onClick={handleUpload}
            disabled={isLoading}
          >
            {isLoading ? "Importing..." : "Import"}
          </button>
        </div>
      </div>
      <BackDrop />
    </>
  );
};

const PMS = () => {
  const breadcrumbs = ["Home", "Master", "PMS"];
  const [pmsData, setPmsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [filters, setFilters] = useState({ pms: "", scheme: "" });
  const [popup, setPopup] = useState({ type: "", mode: "", data: null });
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [syncClicked, setSyncClicked] = useState([]);

  const triggerRerender = () => setRenderKey((prev) => prev + 1);

  const fetchPmsData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${pmsApiBaseUrl}/list_all/`, {
        headers: getHeaders(),
      });
      const resData = res.data;
      if (!resData?.error) {
        setPmsData(
          Array.isArray(resData)
            ? resData
            : resData?.data || resData?.results || [],
        );
      } else {
        errorNotify(resData?.message || "Unable to fetch PMS data.");
      }
    } catch (err) {
      errorNotify("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPmsData();
  }, [renderKey]);

  const pmsFilterOptions = useMemo(
    () =>
      pmsData.map((pms) => ({
        ...pms,
        filter_name: getPmsName(pms),
      })),
    [pmsData],
  );

  const schemeFilterOptions = useMemo(
    () =>
      pmsData.flatMap((pms) =>
        getPmsSchemes(pms).map((scheme) => ({
          ...scheme,
          filter_name: getSchemeName(scheme),
          pms_id: pms.id,
          pms_name: getPmsName(pms),
        })),
      ),
    [pmsData],
  );

  const filteredData = useMemo(() => {
    return pmsData.filter((pms) => {
      const pmsMatch = filters.pms
        ? String(pms.id) === String(filters.pms)
        : true;
      const schemeMatch = filters.scheme
        ? getPmsSchemes(pms).some(
            (scheme) => String(scheme.id) === String(filters.scheme),
          )
        : true;
      return pmsMatch && schemeMatch;
    });
  }, [pmsData, filters]);

  const handleImportPms = async (formData) => {
    try {
      const res = await axios.post(`${pmsApiBaseUrl}/upload/`, formData, {
        headers: getHeaders("multipart/form-data"),
      });
      if (!res.data?.error) {
        successNotify(res.data?.message || "PMS imported successfully.");
        triggerRerender();
        return true;
      }
      errorNotify(res.data?.message || "Import failed.");
      return false;
    } catch (err) {
      errorNotify("Something went wrong.");
      return false;
    }
  };

  const submitPms = async (payload) => {
    setIsButtonClicked(true);
    try {
      const api = payload.id
        ? `${pmsApiBaseUrl}/update_pms/`
        : `${pmsApiBaseUrl}/save_pms/`;
      const res = await axios.post(api, payload, { headers: getHeaders() });
      if (!res.data?.error) {
        successNotify(res.data?.message || "Data saved.");
        triggerRerender();
        return true;
      }
      errorNotify(res.data?.message || "Unable to save PMS.");
      return false;
    } catch (err) {
      errorNotify("Something went wrong.");
      return false;
    } finally {
      setIsButtonClicked(false);
    }
  };

  const handleSyncMaster = async (syncDataTo) => {
    if (syncClicked.includes(syncDataTo)) return;

    setSyncClicked((prev) => [...prev, syncDataTo]);
    try {
      const res = await axios.post(
        `${pmsApiBaseUrl}/sync_master_to_instance/`,
        { sync_data_to: syncDataTo },
        { headers: getHeaders() },
      );

      if (!res.data?.error) {
        successNotify(
          res.data?.message || `PMS master synced to ${syncDataTo}.`,
        );
        return true;
      }
      errorNotify(res.data?.message || `Unable to sync to ${syncDataTo}.`);
      return false;
    } catch (err) {
      errorNotify("Something went wrong.");
      return false;
    } finally {
      setSyncClicked((prev) => prev.filter((item) => item !== syncDataTo));
    }
  };

  const submitScheme = async (payload) => {
    setIsButtonClicked(true);
    try {
      const api = payload.id
        ? `${pmsApiBaseUrl}/update_scheme/`
        : `${pmsApiBaseUrl}/create_scheme/`;
      const res = await axios.post(api, payload, { headers: getHeaders() });
      if (!res.data?.error) {
        successNotify(res.data?.message || "Data saved.");
        triggerRerender();
        return true;
      }
      errorNotify(res.data?.message || "Unable to save scheme.");
      return false;
    } catch (err) {
      errorNotify("Something went wrong.");
      return false;
    } finally {
      setIsButtonClicked(false);
    }
  };

  const getVisibleSchemes = (pms) =>
    filters.scheme
      ? getPmsSchemes(pms).filter(
          (scheme) => String(scheme.id) === String(filters.scheme),
        )
      : getPmsSchemes(pms);

  const tableRows = filteredData.map((pms) => ({
    ...pms,
    __rowType: "pms",
    subRows: getVisibleSchemes(pms).map((scheme, index) => ({
      ...scheme,
      __rowType: "scheme",
      __parentPms: pms,
      __schemeIndex: index,
      pms: scheme.pms || pms.id,
    })),
  }));

  const columns = [
    {
      id: "srNo",
      header: "S. NO.",
      size: 70,
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row }) =>
        row.original.__rowType === "scheme"
          ? `${row.original.__schemeIndex + 1}.`
          : `${row.index + 1}.`,
    },
    {
      id: "pms",
      accessorFn: (row) =>
        row.__rowType === "scheme"
          ? getPmsName(row.__parentPms)
          : getPmsName(row),
      header: "PMS",
      size: 220,
    },
    {
      id: "licenseNumber",
      header: "LICENSE NUMBER",
      size: 180,
      Cell: ({ row }) =>
        row.original.__rowType === "pms"
          ? row.original.license_number || "-"
          : "-",
    },
    {
      id: "schemeName",
      header: "SCHEME NAME",
      size: 260,
      Cell: ({ row }) =>
        row.original.__rowType === "scheme" ? getSchemeName(row.original) : "-",
    },
    {
      id: "code",
      header: "CODE",
      size: 160,
      Cell: ({ row }) =>
        row.original.__rowType === "scheme" ? getSchemeCode(row.original) : "-",
    },
    {
      id: "status",
      header: "STATUS",
      size: 120,
      Cell: ({ row }) => getStatusLabel(row.original.status),
    },
    {
      id: "action",
      header: "ACTION",
      enableSorting: false,
      enableColumnActions: false,
      size: 140,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const isScheme = row.original.__rowType === "scheme";

        return (
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() =>
                setPopup({
                  type: isScheme ? "scheme" : "pms",
                  mode: "edit",
                  data: isScheme ? row.original : row.original,
                })
              }
            >
              <img src={editPencilIcon} alt="edit" className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() =>
                setPopup({
                  type: isScheme ? "scheme" : "pms",
                  mode: "view",
                  data: isScheme ? row.original : row.original,
                })
              }
            >
              <img src={visibilityIcon} alt="view" className="h-5 w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  const actionButtons = [
    {
      id: "import-pms",
      name: "Import PMS",
      image: contractImportIcon,
      actionHandler: () => setPopup({ type: "import" }),
    },
    {
      id: "add-pms",
      name: "Add PMS",
      image: contractAddIcon,
      actionHandler: () =>
        setPopup({ type: "pms", mode: "add", data: initialPmsForm }),
    },
    {
      id: "add-scheme",
      name: "Add Scheme",
      image: contractAddIcon,
      actionHandler: () =>
        setPopup({ type: "scheme", mode: "add", data: initialSchemeForm }),
    },
  ];

  return (
    <div className="app-container min-h-[65vh] pb-[60px] pt-[100px]">
      <Breadcrumbs items={breadcrumbs} />

      <div className="mt-6 items-end justify-between gap-4">
        <div className="flex flex-wrap gap-6 mb-4">
          <div className="flex w-[220px] flex-col gap-2 text-neutral-600">
            <label className="text-[14px] font-[400] text-[#868686]">PMS</label>
            <SearchableDropdown
              data={pmsFilterOptions}
              valueKey="id"
              displayKey="filter_name"
              filterKey="pms"
              setFilter={(val) =>
                setFilters((prev) => ({ ...prev, pms: val || "" }))
              }
              label="PMS"
              type="searchable"
              loading={loading}
            />
          </div>
          <div className="flex w-[220px] flex-col gap-2 text-neutral-600">
            <label className="text-[14px] font-[400] text-[#868686]">
              Scheme Name
            </label>
            <SearchableDropdown
              data={schemeFilterOptions}
              valueKey="id"
              displayKey="filter_name"
              filterKey="scheme"
              setFilter={(val) =>
                setFilters((prev) => ({ ...prev, scheme: val || "" }))
              }
              label="Scheme Name"
              type="searchable"
              loading={loading}
            />
          </div>
        </div>

        <div className="flex justify-end items-center gap-4 w-full max-sm:justify-between max-sm:[&>div]:w-[48%]">
          {actionButtons.map((action, idx) => (
            <div key={action.id} className="flex items-center gap-4">
              {idx !== 0 && (
                <div className="h-[35px] w-[2px] bg-neutral-200 max-lg:hidden" />
              )}
              <button
                className="flex items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-6 py-1 font-bold text-[#636363] max-sm:w-full"
                onClick={action.actionHandler}
                type="button"
              >
                <img src={action.image} alt="i4Invest" />
                {action.name}
              </button>
            </div>
          ))}
          {["Dev", "Test", "Prod"].map((syncType) => (
            <button
              key={syncType}
              type="button"
              onClick={() => handleSyncMaster(syncType)}
              disabled={syncClicked.includes(syncType)}
              className={`${
                syncClicked.includes(syncType)
                  ? "cursor-not-allowed opacity-[50%]"
                  : ""
              } flex min-w-[120px] items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-2 py-1 font-bold text-[#636363]`}
            >
              <FaSync
                className={syncClicked.includes(syncType) ? "animate-spin" : ""}
              />
              {syncType.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Table
          tableData={tableRows}
          columns={columns}
          loading={loading}
          pageName="PMS"
          userId={JSON.parse(sessionStorage.getItem("user"))?.userId}
          tableOptions={{
            enableExpanding: true,
            enableExpandAll: false,
            displayColumnDefOptions: {
              "mrt-row-expand": {
                header: "",
                Header: () => null,
                size: 58,
              },
            },
            getSubRows: (row) => row.subRows,
            getRowCanExpand: (row) =>
              row.original.__rowType === "pms" &&
              row.original.subRows?.length > 0,
            mantineTableBodyRowProps: ({ row }) => ({
              className:
                row.original.__rowType === "scheme" ? "bg-neutral-50" : "",
            }),
          }}
        />
      </div>

      {popup.type === "import" && (
        <ImportPmsPopup
          onClose={() => setPopup({})}
          onSubmit={handleImportPms}
        />
      )}
      {popup.type === "pms" && (
        <PmsFormPopup
          mode={popup.mode}
          data={popup.data}
          onClose={() => setPopup({})}
          onSubmit={submitPms}
          isButtonClicked={isButtonClicked}
        />
      )}
      {popup.type === "scheme" && (
        <SchemeFormPopup
          mode={popup.mode}
          data={popup.data}
          pmsOptions={pmsData}
          onClose={() => setPopup({})}
          onSubmit={submitScheme}
          isButtonClicked={isButtonClicked}
        />
      )}
    </div>
  );
};

export default PMS;
